import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Login from "./components/Login";
import GuardDashboard from "./components/GuardDashboard";
import ResidentDashboard from "./components/ResidentDashboard";
import AdminDashboard from "./components/AdminDashboard";
import VisitorQR from "./components/VisitorQR";
import Landing from "./components/Landing";
import Solicitar from "./components/Solicitar";
import Adquirir from "./components/Adquirir";
import Residenciales from "./components/Residenciales";
import useAuthStore from "./store/useAuthStore";
import useThemeStore from "./store/useThemeStore";
import ErrorBoundary from "./components/ErrorBoundary";
import socketManager from "./api/socket";
import NotificationToastListener from "./components/NotificationToastListener";
// import TestSocket from "./components/TestSocket";

// Simple Loading Component
function Loading({ text = "Cargando..." }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: '1rem'
    }}>
      <div className="animate-spin" style={{
        width: '3rem',
        height: '3rem',
        border: '3px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        borderRadius: '50%'
      }}></div>
      <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>{text}</p>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <Loading text="Verificando autenticación..." />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const redirectPath = {
      resident: '/resident',
      guard: '/guard',
      admin: '/admin',
      superadmin: '/residenciales',
    }[user.role] || '/login';
    
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

// Auto redirect based on role
function AuthRedirect() {
  const { user, isAuthenticated } = useAuthStore();

  // Quien no ha entrado ve la portada del producto, no la pantalla de acceso:
  // la raíz del dominio es donde llega quien todavía no es cliente.
  if (!isAuthenticated || !user) {
    return <Landing />;
  }

  const redirectPath = {
    resident: '/resident',
    guard: '/guard',
    admin: '/admin',
    superadmin: '/residenciales',
  }[user.role];

  return <Navigate to={redirectPath} replace />;
}

function App() {
  const { isAuthenticated } = useAuthStore();
  const { initializeTheme } = useThemeStore();

  // Initialize theme on mount
  useEffect(() => {
    const cleanup = initializeTheme();
    return cleanup;
  }, [initializeTheme]);

  // Manage socket connection based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      socketManager.connect();
    } else {
      socketManager.disconnect();
    }
    
    return () => {
      // Cleanup on unmount
      socketManager.disconnect();
    };
  }, [isAuthenticated]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/adquirir" element={<Adquirir />} />
            <Route path="/solicitar" element={<Solicitar />} />
            <Route path="/visitor-qr/:qrCode" element={<VisitorQR />} />
            <Route path="/v/:shortId" element={<VisitorQR />} />
            
            {/* Protected routes */}
            <Route
              path="/resident"
              element={
                <ProtectedRoute allowedRoles={["resident"]}>
                  <ResidentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guard"
              element={
                <ProtectedRoute allowedRoles={["guard"]}>
                  <GuardDashboard />
                </ProtectedRoute>
              }
            />
            {/* Plataforma: alta de residenciales. Solo el superadministrador,
                y el servidor lo vuelve a comprobar en cada petición. */}
            <Route
              path="/residenciales"
              element={
                <ProtectedRoute allowedRoles={["superadmin"]}>
                  <Residenciales />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin", "superadmin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Root path - redirect based on role */}
            <Route path="/" element={<AuthRedirect />} />
            
            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
        
        {/* Socket debugger in development */}
        {/* {process.env.NODE_ENV === 'development' && isAuthenticated && <TestSocket />} */}
        
        {/* Notification Toast Listener - Always active when authenticated */}
        {isAuthenticated && <NotificationToastListener />}
        
        {/* Toast notifications */}
        {/* El ÚNICO contenedor de avisos de la aplicación. Había un segundo
          * en `AdminDashboard`, y en esa pantalla cada mensaje se dibujaba
          * dos veces. El aspecto se define en `index.css`, con los tokens del
          * tema, para que funcione igual en claro y en oscuro. */}
        <ToastContainer
          position="top-right"
          autoClose={4000}
          /* Con avisos en tiempo real por Socket.IO, una ráfaga —cinco
           * visitas que llegan juntas— llenaba la pantalla de tarjetas y
           * tapaba la propia lista que se acababa de actualizar. */
          limit={3}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          closeButton={false}
        />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;